import { useState, useMemo } from "react";
import { z } from "zod";
import { RichText } from "../RichText";
import { useRenderContext } from "../RenderContext";
import { globalSectionSchema, subHeadingLevel } from "../globalProps";
import { SectionShell } from "../SectionShell";

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Frequently Asked Questions"),
  layout: z.enum(["accordion", "two-column"]).default("accordion"),
  maxItems: z.number().int().min(1).nullable().default(null),
  includedFaqIds: z.array(z.string()).nullable().default(null),
  showSearch: z.boolean().default(false),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

/* ---------- Layout: Accordion (default) ---------- */

function AccordionLayout({ items }: { items: FaqItem[] }) {
  return (
    <div className="space-y-4">
      {items.map((item) => (
        <details
          key={item.id}
          className="group bg-white rounded-lg shadow-md"
        >
          <summary className="cursor-pointer list-none flex items-center justify-between p-5 font-semibold text-text">
            <span>{item.question}</span>
            <span className="ml-4 shrink-0 text-text-muted transition-transform group-open:rotate-45 text-xl leading-none">
              +
            </span>
          </summary>
          <div className="px-5 pb-5 text-text-muted leading-relaxed">
            {item.answer}
          </div>
        </details>
      ))}
    </div>
  );
}

/* ---------- Layout: Two-column (Q left, A right) ---------- */

function TwoColumnLayout({ items, SubH }: { items: FaqItem[]; SubH: "h2" | "h3" | "h4" }) {
  return (
    <div className="space-y-8">
      {items.map((item) => (
        <div
          key={item.id}
          className="grid grid-cols-1 @md:grid-cols-2 gap-4 @md:gap-10 pb-8 border-b border-current/10 last:border-b-0 last:pb-0"
        >
          <SubH className="text-lg font-semibold">
            {item.question}
          </SubH>
          <p className="opacity-80 leading-relaxed">
            {item.answer}
          </p>
        </div>
      ))}
    </div>
  );
}

/** Strip HTML tags to get plain text for search matching. */
function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "");
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const ctx = useRenderContext();
  const allItems: FaqItem[] = ctx.faqItems || [];
  // When includedFaqIds is set, filter to only those FAQ items (preserving
  // the order from the array). When null, show all (current behavior).
  const filteredItems = safe.includedFaqIds
    ? safe.includedFaqIds
        .map((id) => allItems.find((item) => item.id === id))
        .filter((item): item is FaqItem => !!item)
    : allItems;
  const items = safe.maxItems ? filteredItems.slice(0, safe.maxItems) : filteredItems;
  const SubH = subHeadingLevel(safe.headingLevel);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const displayItems = useMemo(() => {
    if (!safe.showSearch || !searchTerm.trim()) return items;
    const needle = searchTerm.trim().toLowerCase();
    return items.filter(
      (item) =>
        item.question.toLowerCase().includes(needle) ||
        stripHtml(item.answer).toLowerCase().includes(needle),
    );
  }, [items, searchTerm, safe.showSearch]);

  if (items.length === 0) {
    if (!ctx.isEditor) return null;
    return (
      <SectionShell globals={safe} widthScale="narrow">
          <div className="text-center">
            <RichText
              as={safe.headingLevel}
              html={safe.heading}
              className="text-3xl @md:text-[2.5rem] font-bold mb-6"
              data-prop="heading"
            />
            <p className="text-text-muted">No FAQs published yet.</p>
          </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell globals={safe} widthScale="narrow">
        <RichText
          as={safe.headingLevel}
          html={safe.heading}
          className="text-3xl @md:text-[2.5rem] font-bold text-center mb-12"
          data-prop="heading"
        />
        {safe.showSearch && (
          <div className="mb-8 max-w-lg mx-auto">
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search questions..."
              className="w-full bg-transparent border border-current/20 rounded-lg px-4 py-3 text-current placeholder-current/50 focus:outline-none focus:border-current/40 transition-colors"
            />
          </div>
        )}
        {displayItems.length === 0 ? (
          <p className="text-center opacity-60">No matching questions found.</p>
        ) : safe.layout === "two-column" ? (
          <TwoColumnLayout items={displayItems} SubH={SubH} />
        ) : (
          <AccordionLayout items={displayItems} />
        )}
    </SectionShell>
  );
}
